import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Truck, Phone, Mail, Globe, MapPin } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var supplierTypes = ["contractor", "vendor", "maintenance", "utility", "insurance", "other"];

var emptyForm = {
  name: "",
  supplier_type: "contractor",
  contact_person: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  postal_code: "",
  country: "",
  website: "",
  notes: "",
  is_active: true,
};

export default function Suppliers() {
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var [suppliers, setSuppliers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [editingSupplier, setEditingSupplier] = useState(null);
  var [form, setForm] = useState(emptyForm);
  var [search, setSearch] = useState("");
  var [filterType, setFilterType] = useState("all");

  var canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  var isAdmin = user?.role === "admin";

  useEffect(function() {
    fetchSuppliers();
  }, []);

  async function fetchSuppliers() {
    try {
      var response = await fetch(API_URL + "/api/suppliers", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setSuppliers(await response.json());
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      var url = editingSupplier ? API_URL + "/api/suppliers/" + editingSupplier.supplier_id : API_URL + "/api/suppliers";
      var method = editingSupplier ? "PUT" : "POST";

      var response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
        credentials: "include",
        body: JSON.stringify(form),
      });

      if (response.ok) {
        toast.success(editingSupplier ? t("suppliers.updated") : t("suppliers.created"));
        setDialogOpen(false);
        setEditingSupplier(null);
        setForm(emptyForm);
        fetchSuppliers();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function handleEdit(supplier) {
    setEditingSupplier(supplier);
    setForm({
      name: supplier.name || "",
      supplier_type: supplier.supplier_type || "contractor",
      contact_person: supplier.contact_person || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      address: supplier.address || "",
      city: supplier.city || "",
      postal_code: supplier.postal_code || "",
      country: supplier.country || "",
      website: supplier.website || "",
      notes: supplier.notes || "",
      is_active: supplier.is_active !== false,
    });
    setDialogOpen(true);
  }

  async function handleDelete(supplierId) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/suppliers/" + supplierId, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        toast.success(t("suppliers.deleted"));
        fetchSuppliers();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function getFilteredSuppliers() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < suppliers.length; i++) {
      var s = suppliers[i];
      var matchName = s.name && s.name.toLowerCase().indexOf(searchLower) >= 0;
      var matchContact = s.contact_person && s.contact_person.toLowerCase().indexOf(searchLower) >= 0;
      var matchCity = s.city && s.city.toLowerCase().indexOf(searchLower) >= 0;
      var matchSearch = matchName || matchContact || matchCity;
      var matchType = filterType === "all" || s.supplier_type === filterType;
      if (matchSearch && matchType) result.push(s);
    }
    return result;
  }

  function renderSupplierTypeOptions() {
    var items = [];
    for (var i = 0; i < supplierTypes.length; i++) {
      items.push(<SelectItem key={supplierTypes[i]} value={supplierTypes[i]}>{t("suppliers.types." + supplierTypes[i])}</SelectItem>);
    }
    return items;
  }

  function renderFilterTypeOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < supplierTypes.length; i++) {
      items.push(<SelectItem key={supplierTypes[i]} value={supplierTypes[i]}>{t("suppliers.types." + supplierTypes[i])}</SelectItem>);
    }
    return items;
  }

  function getTypeBadge(type) {
    var colors = {
      contractor: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      vendor: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
      maintenance: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
      utility: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      insurance: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400",
      other: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
    };
    return <Badge className={colors[type] || colors.other}>{t("suppliers.types." + type)}</Badge>;
  }

  function renderSupplierRows() {
    var filteredSuppliers = getFilteredSuppliers();
    var rows = [];
    for (var i = 0; i < filteredSuppliers.length; i++) {
      var supplier = filteredSuppliers[i];
      var supplierId = supplier.supplier_id;
      var supplierData = JSON.parse(JSON.stringify(supplier));
      rows.push(
        <TableRow key={supplierId} data-testid={"supplier-row-" + supplierId}>
          <TableCell className="font-medium">{supplier.name}</TableCell>
          <TableCell>{getTypeBadge(supplier.supplier_type)}</TableCell>
          <TableCell>{supplier.contact_person || "-"}</TableCell>
          <TableCell>
            {supplier.email ? (
              <a href={"mailto:" + supplier.email} className="text-primary hover:underline flex items-center gap-1">
                <Mail className="w-3 h-3" />{supplier.email}
              </a>
            ) : "-"}
          </TableCell>
          <TableCell>{supplier.phone || "-"}</TableCell>
          <TableCell>{supplier.city || "-"}</TableCell>
          <TableCell>
            <Badge variant={supplier.is_active ? "default" : "secondary"}>
              {supplier.is_active ? t("suppliers.active") : t("suppliers.inactive")}
            </Badge>
          </TableCell>
          {isAdmin && (
            <TableCell>
              {supplier.owner_name ? (
                <div>
                  <div className="font-medium text-sm">{supplier.owner_name}</div>
                  <div className="text-xs text-muted-foreground">{supplier.owner_email}</div>
                </div>
              ) : "-"}
            </TableCell>
          )}
          {canEdit && (
            <TableCell>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={handleEdit.bind(null, supplierData)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={handleDelete.bind(null, supplierId)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </TableCell>
          )}
        </TableRow>
      );
    }
    return rows;
  }

  var filteredSuppliers = getFilteredSuppliers();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("suppliers.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("suppliers.subtitle")}</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={function(open) { setDialogOpen(open); if (!open) { setEditingSupplier(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-supplier-btn"><Plus className="w-4 h-4 mr-2" />{t("suppliers.addSupplier")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSupplier ? t("suppliers.editSupplier") : t("suppliers.addNewSupplier")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="form-label">{t("common.name")} *</Label>
                    <Input value={form.name} onChange={function(e) { setForm({...form, name: e.target.value}); }} required data-testid="supplier-name-input" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="form-label">{t("common.type")}</Label>
                    <Select value={form.supplier_type} onValueChange={function(v) { setForm({...form, supplier_type: v}); }}>
                      <SelectTrigger data-testid="supplier-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderSupplierTypeOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("suppliers.contactPerson")}</Label>
                    <Input value={form.contact_person} onChange={function(e) { setForm({...form, contact_person: e.target.value}); }} />
                  </div>
                  <div>
                    <Label className="form-label">{t("common.email")}</Label>
                    <Input type="email" value={form.email} onChange={function(e) { setForm({...form, email: e.target.value}); }} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("suppliers.phone")}</Label>
                    <Input value={form.phone} onChange={function(e) { setForm({...form, phone: e.target.value}); }} />
                  </div>
                  <div>
                    <Label className="form-label">{t("suppliers.website")}</Label>
                    <Input value={form.website} onChange={function(e) { setForm({...form, website: e.target.value}); }} placeholder="https://" />
                  </div>
                </div>

                <div>
                  <Label className="form-label">{t("suppliers.address")}</Label>
                  <Input value={form.address} onChange={function(e) { setForm({...form, address: e.target.value}); }} />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="form-label">{t("suppliers.city")}</Label>
                    <Input value={form.city} onChange={function(e) { setForm({...form, city: e.target.value}); }} />
                  </div>
                  <div>
                    <Label className="form-label">{t("suppliers.postalCode")}</Label>
                    <Input value={form.postal_code} onChange={function(e) { setForm({...form, postal_code: e.target.value}); }} />
                  </div>
                  <div>
                    <Label className="form-label">{t("suppliers.country")}</Label>
                    <Input value={form.country} onChange={function(e) { setForm({...form, country: e.target.value}); }} />
                  </div>
                </div>

                <div>
                  <Label className="form-label">{t("common.notes")}</Label>
                  <Textarea value={form.notes} onChange={function(e) { setForm({...form, notes: e.target.value}); }} rows={3} />
                </div>

                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="is_active" 
                    checked={form.is_active} 
                    onChange={function(e) { setForm({...form, is_active: e.target.checked}); }}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">{t("suppliers.isActive")}</Label>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button>
                  <Button type="submit" data-testid="supplier-submit-btn">{t("common.save")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t("suppliers.searchPlaceholder")} value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" data-testid="supplier-search" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-type-select">
            <SelectValue placeholder={t("common.type")} />
          </SelectTrigger>
          <SelectContent>{renderFilterTypeOptions()}</SelectContent>
        </Select>
      </div>

      {filteredSuppliers.length > 0 ? (
        <Card className="bento-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.type")}</TableHead>
                <TableHead>{t("suppliers.contactPerson")}</TableHead>
                <TableHead>{t("common.email")}</TableHead>
                <TableHead>{t("suppliers.phone")}</TableHead>
                <TableHead>{t("suppliers.city")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                {isAdmin && <TableHead>{t("common.owner")}</TableHead>}
                {canEdit && <TableHead className="w-20">{t("common.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>{renderSupplierRows()}</TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("suppliers.noSuppliers")}</p>
            {canEdit && <Button className="mt-4" onClick={function() { setDialogOpen(true); }}>{t("suppliers.addFirstSupplier")}</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
